import React, { useState,useEffect,useRef,useMemo } from "react";
import { FaSearch } from "react-icons/fa";
import { FaChevronRight } from "react-icons/fa";

interface SearchableListProps {
  items: string[];
  selectedItems: string[];
  onSelectionChange: (selectedItems: string[]) => void;
  disabled?: boolean; 
  onItemToggle?: (item: string, nextSelected: boolean) => void;
  onRowActionClick?: (item: string) => void;
  rowActionTitle?: string;
  activeRowActionItem?: string | null;
  rowActionDisabled?: boolean;
  preserveItemOrder?: boolean;
  reorderSelectedOnOutside?: boolean;
  dividerBeforeItem?: string | null;
  sectionDividers?: Array<{ beforeItem: string; label: string }>;
}

const SearchableList: React.FC<SearchableListProps> = ({
  items,
  selectedItems,
  onSelectionChange,
  disabled = false,
  onItemToggle,
  onRowActionClick,
  rowActionTitle = "View item details",
  activeRowActionItem = null,
  rowActionDisabled = false,
  preserveItemOrder = true,
  reorderSelectedOnOutside = false,
  dividerBeforeItem = null,
  sectionDividers = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFirstMode, setSelectedFirstMode] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSearchTerm("");
        if (reorderSelectedOnOutside) {
          setSelectedSnapshot(selectedItems);
          setSelectedFirstMode(true);
        }
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [reorderSelectedOnOutside, selectedItems]);

  useEffect(() => {
    if (disabled && reorderSelectedOnOutside) {
      setSelectedSnapshot(selectedItems);
      setSelectedFirstMode(true);
    }
  }, [disabled, reorderSelectedOnOutside, selectedItems]);

  
  // const filteredItems = items.filter((item) =>
  //   item.toLowerCase().includes(searchTerm.toLowerCase())
  // );
  const filteredItems = useMemo(() => {
    const query = searchTerm.toLowerCase();
  
    const matchesQuery = (item: string) =>
      item.toLowerCase().includes(query);

    if (selectedFirstMode && reorderSelectedOnOutside) {
      const snapshotSet = new Set(selectedSnapshot);

      if (preserveItemOrder) {
        const selected = items.filter((item) => snapshotSet.has(item) && matchesQuery(item));
        const unselected = items.filter((item) => !snapshotSet.has(item) && matchesQuery(item));
        return [...selected, ...unselected];
      }

      const selected = items
        .filter((item) => snapshotSet.has(item) && matchesQuery(item))
        .sort((a, b) => a.localeCompare(b));

      const unselected = items
        .filter((item) => !snapshotSet.has(item) && matchesQuery(item))
        .sort((a, b) => a.localeCompare(b));

      return [...selected, ...unselected];
    }

    if (preserveItemOrder) {
      return items.filter((item) => matchesQuery(item));
    }
  
    const selectedSet = new Set(selectedItems);
  
    const selected = items
      .filter(item => selectedSet.has(item) && matchesQuery(item))
      .sort((a, b) => a.localeCompare(b));
  
    const unselected = items
      .filter(item => !selectedSet.has(item) && matchesQuery(item))
      .sort((a, b) => a.localeCompare(b));
  
    return [...selected, ...unselected];
  }, [
    items,
    selectedItems,
    searchTerm,
    preserveItemOrder,
    reorderSelectedOnOutside,
    selectedFirstMode,
    selectedSnapshot,
  ]);
  

  const toggleItemSelection = (item: string) => {
    const updatedSelection = selectedItems.includes(item)
      ? selectedItems.filter((i) => i !== item)
      : [...selectedItems, item];
    onSelectionChange(updatedSelection);
  };

  const dividerByItem = useMemo(() => {
    const map = new Map<string, string>();

    sectionDividers.forEach((divider) => {
      if (divider?.beforeItem) {
        map.set(divider.beforeItem, divider.label || "");
      }
    });

    if (dividerBeforeItem && !map.has(dividerBeforeItem)) {
      map.set(dividerBeforeItem, "");
    }

    return map;
  }, [sectionDividers, dividerBeforeItem]);
  
  // console.log("Selected Items:", selectedItems);
  
  
  return (
    <div ref={wrapperRef} className="space-y-4">
      {/* Search Input */}
      <div className="relative w-full max-w-md ">
        <FaSearch className="absolute top-3.5 left-3 text-gray-500 w-5 h-5" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          // onBlur={() => setSearchTerm("")}
          className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:border-blue-500 bg-gray-50 text-gray-800 shadow-inner text-sm cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Search here"
          disabled={disabled}  
          
        />
      </div>

      {/* Filtered Items List */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow max-h-96 overflow-y-auto">
        {filteredItems.length > 0 ? (
          filteredItems.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              {dividerByItem.has(item) && index > 0 && (
                <div className="my-2 flex items-center">
                  <div className="flex-1 border-t border-gray-300" />
                  {dividerByItem.get(item) && (
                    <span className="px-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      {dividerByItem.get(item)}
                    </span>
                  )}
                  <div className="flex-1 border-t border-gray-300" />
                </div>
              )}
              <div
                className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                  activeRowActionItem === item ? "bg-blue-50" : "hover:bg-blue-50"
                }`}
              >
                <label className="flex items-center space-x-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item)}
                    onChange={() => {
                      const nextSelected = !selectedItems.includes(item);
                      if (onItemToggle) {
                        onItemToggle(item, nextSelected);
                      } else {
                        toggleItemSelection(item);
                      }
                    }}
                    className="peer hidden"
                    disabled={disabled}
                  />
                  <div className="w-5 h-5 border-2 rounded-md border-gray-400 flex items-center justify-center peer-checked:bg-blue-500 peer-checked:border-blue-500">
                    {selectedItems.includes(item) && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3.5 h-3.5 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-gray-800 text-sm font-medium">{item}</span>
                </label>

                {onRowActionClick && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRowActionClick(item);
                    }}
                    disabled={rowActionDisabled}
                    title={rowActionTitle}
                    className="ml-3 p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </React.Fragment>
          ))
        ) : (
          <p className="text-gray-500 text-sm text-center">No items found.</p>
        )}
      </div>
    </div>
  );
};

export default SearchableList;
